import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { CalendarDays, AlertTriangle, ChevronLeft, ChevronRight, Filter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DAY_LABELS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

export default function LeavePlannerTab() {
  const [calData, setCalData] = useState({ holidays: [], leaves: [] });
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deptFilter, setDeptFilter] = useState('all');
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());

  useEffect(() => {
    api.get('/api/leave/departments').then(r => setDepartments(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    api.get('/api/dashboard/calendar-data', { params: { month, year } })
      .then(r => setCalData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [month, year]);

  const prevMonth = () => { if (month === 1) { setMonth(12); setYear(y => y - 1); } else setMonth(m => m - 1); };
  const nextMonth = () => { if (month === 12) { setMonth(1); setYear(y => y + 1); } else setMonth(m => m + 1); };

  const daysInMonth = new Date(year, month, 0).getDate();
  const firstDay = new Date(year, month - 1, 1).getDay();
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  const holidayMap = {};
  (calData.holidays || []).forEach(h => {
    const d = parseInt(h.date.split('-')[2]);
    holidayMap[d] = h;
  });

  const leaveMap = {};
  const filteredLeaves = deptFilter === 'all' ? (calData.leaves || []) : (calData.leaves || []).filter(lv => lv.department === deptFilter);
  filteredLeaves.forEach(lv => {
    const start = new Date(lv.start_date + 'T00:00:00');
    const end = new Date(lv.end_date + 'T00:00:00');
    for (let dt = new Date(start); dt <= end; dt.setDate(dt.getDate() + 1)) {
      if (dt.getMonth() + 1 === month && dt.getFullYear() === year) {
        const d = dt.getDate();
        if (!leaveMap[d]) leaveMap[d] = [];
        leaveMap[d].push(lv);
      }
    }
  });

  // Find conflicts (multiple team members on leave same day)
  const conflicts = Object.entries(leaveMap).filter(([, lvs]) => lvs.length > 1).map(([d, lvs]) => ({
    day: parseInt(d),
    count: lvs.length,
    names: lvs.map(l => l.employee_name),
  }));

  if (loading) return <Skeleton className="h-96" />;

  return (
    <div className="space-y-4" data-testid="leave-planner">
      {/* Calendar Header */}
      <Card className="border-slate-200">
        <CardHeader className="py-3 px-4 pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <CalendarDays className="w-4 h-4 text-slate-500" /> Team Leave Calendar
          </CardTitle>
          <div className="flex items-center gap-2">
            <Select value={deptFilter} onValueChange={setDeptFilter}>
              <SelectTrigger className="w-[140px] h-7 text-xs" data-testid="planner-dept-filter">
                <Filter className="w-3 h-3 mr-1 text-slate-400" /><SelectValue placeholder="All Depts" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Departments</SelectItem>
                {departments.map(d => (
                  <SelectItem key={d} value={d}>{d}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="ghost" size="sm" onClick={prevMonth} className="h-7 w-7 p-0"><ChevronLeft className="w-4 h-4" /></Button>
            <span className="text-sm font-medium w-36 text-center">{MONTHS[month - 1]} {year}</span>
            <Button variant="ghost" size="sm" onClick={nextMonth} className="h-7 w-7 p-0"><ChevronRight className="w-4 h-4" /></Button>
          </div>
        </CardHeader>
        <CardContent className="px-4 pb-4">
          {/* Day Labels */}
          <div className="grid grid-cols-7 gap-1 mb-1">
            {DAY_LABELS.map(d => (
              <div key={d} className={`text-center text-[10px] font-medium py-1 ${d === 'Sun' ? 'text-red-400' : 'text-slate-400'}`}>{d}</div>
            ))}
          </div>
          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-1">
            {Array(firstDay).fill(null).map((_, i) => <div key={`e${i}`} />)}
            {days.map(d => {
              const isHoliday = !!holidayMap[d];
              const isSunday = new Date(year, month - 1, d).getDay() === 0;
              const hasLeave = !!leaveMap[d];
              const isConflict = leaveMap[d]?.length > 1;
              const isToday = d === now.getDate() && month === now.getMonth() + 1 && year === now.getFullYear();
              return (
                <div
                  key={d}
                  className={`relative p-1.5 rounded-lg text-center text-sm transition-colors min-h-[52px]
                    ${isToday ? 'ring-2 ring-[#0D2850] ring-offset-1' : ''}
                    ${isHoliday ? 'bg-red-50 text-red-600' : isSunday ? 'bg-red-50/50 text-red-400' : hasLeave ? 'bg-amber-50' : 'hover:bg-slate-50'}
                  `}
                  title={isHoliday ? holidayMap[d].name : hasLeave ? leaveMap[d].map(l => l.employee_name).join(', ') : ''}
                  data-testid={`cal-day-${d}`}
                >
                  <span className={`text-xs font-medium ${isToday ? 'font-bold' : ''}`}>{d}</span>
                  <div className="flex justify-center gap-0.5 mt-0.5">
                    {isHoliday && <span className="w-1.5 h-1.5 rounded-full bg-red-500" />}
                    {hasLeave && <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />}
                    {isConflict && <span className="w-1.5 h-1.5 rounded-full bg-orange-600" />}
                  </div>
                </div>
              );
            })}
          </div>
          {/* Legend */}
          <div className="flex gap-4 mt-3 text-[10px] text-slate-500">
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500" /> Holiday</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500" /> Leave</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-orange-600" /> Conflict</span>
          </div>
        </CardContent>
      </Card>

      {/* Conflict Indicator */}
      {conflicts.length > 0 && (
        <Card className="border-amber-200 bg-amber-50/50" data-testid="conflict-indicator">
          <CardContent className="p-3">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-medium text-amber-800">Leave Conflicts Detected</p>
                <div className="space-y-1 mt-1">
                  {conflicts.map(c => (
                    <p key={c.day} className="text-xs text-amber-700">
                      {MONTHS[month - 1]} {c.day}: <span className="font-medium">{c.count} team members</span> — {c.names.join(', ')}
                    </p>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Leaves this month */}
      <Card className="border-slate-200">
        <CardHeader className="py-3 px-4 pb-2">
          <CardTitle className="text-sm font-semibold">Leaves This Month</CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-3">
          {filteredLeaves.length === 0 ? (
            <p className="text-xs text-slate-400 py-2">No leaves this month</p>
          ) : (
            <div className="space-y-2">
              {filteredLeaves.map((lv, i) => (
                <div key={i} className="flex items-center justify-between py-1.5 border-b border-slate-50 last:border-0">
                  <div>
                    <p className="text-sm font-medium">{lv.employee_name}</p>
                    <p className="text-[10px] text-slate-400">{lv.start_date} → {lv.end_date}</p>
                  </div>
                  <Badge className={`text-[10px] ${lv.status === 'approved' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'} hover:opacity-90`}>
                    {lv.status} • {lv.leave_type}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
